/**
 * Vercel Blob Storage Utility
 * 
 * Handles file uploads to Vercel Blob storage for:
 * - Custom icons (SVG, PNG, GIF, Lottie)
 * - Background images (PNG, JPG, GIF)
 */

/**
 * Upload a file to Vercel Blob storage
 * 
 * @param {File} file - The file to upload
 * @param {string} folder - Folder path (e.g., 'icons' or 'backgrounds')
 * @returns {Promise<string>} - The blob URL
 */
export async function uploadToBlob(file, folder = 'uploads') {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await fetch('/api/upload', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Error uploading to blob:', error);
    throw error;
  }
}

/**
 * Delete a file from Vercel Blob storage
 * 
 * @param {string} url - The blob URL to delete
 * @returns {Promise<void>}
 */
export async function deleteFromBlob(url) {
  try {
    const response = await fetch('/api/upload', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Delete failed');
    }
  } catch (error) {
    console.error('Error deleting from blob:', error);
    throw error;
  }
}

/**
 * Validate file before upload
 * 
 * @param {File} file - The file to validate
 * @param {object} options - Validation options
 * @returns {object} - { valid: boolean, error?: string }
 */
export function validateFile(file, options = {}) {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'application/json'],
    allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.json'],
  } = options;

  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`,
    };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} is not allowed. Allowed types: ${allowedExtensions.join(', ')}`,
    };
  }

  // Check file extension
  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (!extension || !allowedExtensions.includes(extension)) {
    return {
      valid: false,
      error: `File extension ${extension} is not allowed. Allowed extensions: ${allowedExtensions.join(', ')}`,
    };
  }

  return { valid: true };
}

/**
 * Get a preview URL for a file before upload
 * 
 * @param {File} file - The file to preview
 * @returns {Promise<string>} - Data URL for preview
 */
export function getFilePreview(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
